// "Erase this person's data" (Session ASQ, Stream G, Item 3; ADMIN ONLY, office UI only). Mounted on the Personal data
// requests page below the search results, it reads that page's ONE selection: the identifiers the records were found by
// and the records the admin left ticked. It never keeps a second copy of the selection.
//
// THE FLOW. "Preview erasure" asks the server what would happen to every ticked record (the server runs the search again
// and plans the erasure): three groups, Will be deleted / Will be anonymised / Kept under a legal hold (each hold named,
// with the date it lifts), plus the Drive files, what Xero keeps, and the record of the erasure that will be kept. "Erase"
// opens the two-step red confirmation naming the counts, and the commit carries out EXACTLY the preview the admin saw (its
// reference); if a record changed in the meantime the server refuses and the admin previews again. The result says what
// was done, and lists any Drive file to delete by hand, with a link to it.
//
// A PRIVATE INDIVIDUAL. A customer (and some records like it) can be a company or a private householder, and nothing on
// the record says which. For such a record the preview offers "This is a private individual's own record": ticking it
// means their name and home address on it go too. It changes the plan, so the admin previews again before erasing.
//
// WHAT IT LEAVES, AND WHY. A record that is anonymised keeps any email, phone or name nobody searched for (or that may be
// someone else's): the preview lists each one, masked, under its record with the engine's own reason, so the admin can add
// it to the search and preview again (the engine's PlannedRecord.unresolved).
//
// A RETRY IS THE SAME COMMIT. When Drive files could not be deleted, or the record of the erasure could not be saved, the
// result offers one button ("Try the Drive deletes again", or "Save the erasure record again" when the record is what
// failed) that resends exactly the first commit's request, the preview's reference and its time included. The server
// knows it for a repeat of that commit and does only what is outstanding; it never erases anything twice.
//
// LiftErasureRecord, below, is the other half of RULE 1 for the record an erasure leaves: it removes it, after which the
// person is recorded and imported without a warning.
import { useEffect, useMemo, useState } from "react";
import { ApiError, api } from "../../../lib/api";
import type { GdprErasurePreview, GdprErasureRecord, GdprErasureResult, GdprIdentifiers, GdprRecordRef, GdprTombstoneLiftResult } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../../components/ConfirmDeleteModal";
import { Button } from "../../../components/Button";

const PRIVATE_INDIVIDUAL = "residential_customer";

const keyOf = (r: GdprRecordRef) => `${r.model}:${r.id}`;

/** One commit's request: the records as the admin confirmed them, and the preview's reference and time. */
interface CommitRequest {
  refs: { model: string; id: string; subjects?: string[] }[];
  planHash: string;
  plannedAt: string;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

const sectionCls = "mt-6 max-w-3xl rounded-ele border border-ele-purple-accent bg-ele-surface p-5";
const groupCls = "mt-4 rounded-ele border border-ele-purple-accent p-3";
const noteCls = "mt-3 text-sm text-ele-body";

export function ErasurePanel({ identifiers, records }: { identifiers: GdprIdentifiers; records: GdprRecordRef[] }) {
  const [preview, setPreview] = useState<GdprErasurePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [result, setResult] = useState<GdprErasureResult | null>(null);
  // The records the admin confirmed are a private individual's own (keyed "Model:id"). Changing one moves the plan.
  const [privateRecords, setPrivateRecords] = useState<Set<string>>(new Set());
  const [stale, setStale] = useState(false);
  // The exact request the first commit sent (the preview's reference and time included), so a retry resends it unchanged.
  const [committed, setCommitted] = useState<CommitRequest | null>(null);

  // A different selection or search is a different erasure: whatever was previewed no longer applies.
  const selectionKey = useMemo(() => JSON.stringify([identifiers, records.map(keyOf).sort()]), [identifiers, records]);
  useEffect(() => {
    setPreview(null);
    setResult(null);
    setError(null);
    setStale(false);
    setPrivateRecords(new Set());
    setCommitted(null);
  }, [selectionKey]);

  const refs = useMemo(
    () => records.map((r) => (privateRecords.has(keyOf(r)) ? { model: r.model, id: r.id, subjects: [PRIVATE_INDIVIDUAL] } : { model: r.model, id: r.id })),
    [records, privateRecords],
  );

  async function doPreview() {
    if (records.length === 0) return;
    setPreviewing(true);
    setError(null);
    setResult(null);
    try {
      setPreview(await api.gdpr.erasure.preview(identifiers, refs));
      setStale(false);
    } catch (err) {
      setPreview(null);
      if (err instanceof ApiError && err.status === 403) setError("Only an admin can erase a person's data, and only from this page.");
      else setError(friendlyApiError(err, "The erasure could not be previewed. Please try again."));
    } finally {
      setPreviewing(false);
    }
  }

  function togglePrivate(r: GdprErasureRecord) {
    setPrivateRecords((prev) => {
      const next = new Set(prev);
      const k = keyOf(r);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
    setStale(true);
  }

  async function commit(body: CommitRequest) {
    setErasing(true);
    setError(null);
    try {
      const res = await api.gdpr.erasure.commit(identifiers, body.refs, body.planHash, body.plannedAt);
      setResult(res);
      setCommitted(body);
      setPreview(null);
      setConfirmOpen(false);
    } catch (err) {
      setConfirmOpen(false);
      if (err instanceof ApiError && err.status === 409) {
        setPreview(null);
        setError(friendlyApiError(err, "The records changed since this erasure was previewed. Preview it again before erasing."));
      } else {
        setError(friendlyApiError(err, "The erasure could not be carried out. Nothing was changed. Please try again."));
      }
    } finally {
      setErasing(false);
    }
  }

  const groups = useMemo(() => {
    const rs = preview?.records ?? [];
    return {
      deleted: rs.filter((r) => r.action === "delete"),
      anonymised: rs.filter((r) => r.action === "anonymise"),
      held: rs.filter((r) => r.action === "held"),
      notFound: rs.filter((r) => r.action === "not_found"),
    };
  }, [preview]);

  const changes = groups.deleted.length + groups.anonymised.length;

  const confirmMessage = preview
    ? [
        `This deletes ${plural(groups.deleted.length, "record", "records")} and removes this person's details from ${plural(groups.anonymised.length, "record", "records")}.`,
        groups.held.length > 0 ? `${plural(groups.held.length, "record is kept as it is", "records are kept as they are")} under a legal hold.` : null,
        preview.files.toDelete.length > 0 ? `${plural(preview.files.toDelete.length, "Drive file is", "Drive files are")} deleted too.` : null,
        "This cannot be undone.",
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  function recordRow(r: GdprErasureRecord) {
    return (
      <li key={keyOf(r)} className="text-sm">
        <span className="text-ele-strong">{r.label}</span>
        <span className="mt-0.5 block text-xs text-ele-body">{r.reason}</span>
        {r.action === "anonymise" && r.fields.length > 0 && (
          <span className="mt-0.5 block text-xs text-ele-body">Details removed: {r.fields.map((f) => f.label).join(", ")}.</span>
        )}
        {r.unresolved && r.unresolved.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-xs text-ele-strong" aria-label={`Details kept on ${r.label}`}>
            {r.unresolved.map((u) => (
              <li key={`${u.field}-${u.masked}`}>
                Kept: {u.label} {u.masked}.{" "}
                {u.why === "not_searched"
                  ? "Nobody searched for it: if it is this person's, add it to the search above and search again."
                  : "It may be someone else's: if it is this person's, confirm it on this record and preview again."}
              </li>
            ))}
          </ul>
        )}
        {r.canConfirmPrivateIndividual && (
          <label className="mt-1 flex items-start gap-2 text-xs text-ele-body">
            <input type="checkbox" className="mt-0.5" checked={privateRecords.has(keyOf(r))} onChange={() => togglePrivate(r)} />
            <span>This is a private individual&apos;s own record (a householder, not a business): their name and home address on it go too.</span>
          </label>
        )}
      </li>
    );
  }

  return (
    <section className={sectionCls} aria-label="Erase this person's data">
      <h2 className="font-heading text-xl text-ele-blue">Erase this person&apos;s data</h2>
      <p className="mt-1 text-sm text-ele-body">
        When someone asks for their personal data to be erased. Preview first: it shows, for every ticked record, whether it will be deleted, kept with this
        person&apos;s details removed, or kept as it is because the law or our accounts require it. Nothing changes until you confirm.
      </p>

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => void doPreview()} disabled={previewing || erasing || records.length === 0}>
          {previewing ? "Previewing..." : preview ? "Preview again" : "Preview erasure"}
        </Button>
      </div>
      {records.length === 0 && <p className="mt-2 text-xs text-ele-body">Tick at least one record to preview an erasure.</p>}

      {preview && (
        <div aria-label="Erasure preview">
          {stale && (
            <p role="status" className="mt-3 rounded-ele border border-amber-400 bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              You changed a record&apos;s details. Press Preview again to see the erasure as it now stands before you erase.
            </p>
          )}

          <div className={groupCls}>
            <h3 className="font-heading text-lg text-ele-strong">Will be deleted ({groups.deleted.length})</h3>
            {groups.deleted.length === 0 ? <p className="text-xs text-ele-body">None.</p> : <ul className="mt-2 space-y-2">{groups.deleted.map(recordRow)}</ul>}
          </div>

          <div className={groupCls}>
            <h3 className="font-heading text-lg text-ele-strong">Will be anonymised ({groups.anonymised.length})</h3>
            <p className="text-xs text-ele-body">The record stays; this person&apos;s details on it are replaced with &quot;Erased on&quot; and the date.</p>
            {groups.anonymised.length === 0 ? <p className="text-xs text-ele-body">None.</p> : <ul className="mt-2 space-y-2">{groups.anonymised.map(recordRow)}</ul>}
          </div>

          <div className={groupCls}>
            <h3 className="font-heading text-lg text-ele-strong">Kept under a legal hold ({groups.held.length})</h3>
            {groups.held.length === 0 ? (
              <p className="text-xs text-ele-body">None.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {groups.held.map((r) => (
                  <li key={keyOf(r)} className="text-sm">
                    <span className="text-ele-strong">{r.label}</span>
                    <span className="mt-0.5 block text-xs text-ele-strong">
                      {r.ruleLabel}. {r.liftsOn ? `Kept until ${r.liftsOn}.` : "No date for the hold to lift is known yet."}
                    </span>
                    <span className="mt-0.5 block text-xs text-ele-body">{r.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {(groups.notFound.length > 0 || preview.notChanged.length > 0) && (
            <div className={groupCls}>
              <h3 className="font-heading text-lg text-ele-strong">Not changed ({groups.notFound.length + preview.notChanged.length})</h3>
              <ul className="mt-2 space-y-2">
                {groups.notFound.map((r) => (
                  <li key={keyOf(r)} className="text-sm">
                    <span className="text-ele-strong">{r.label}</span>
                    <span className="mt-0.5 block text-xs text-ele-body">{r.reason}</span>
                  </li>
                ))}
                {preview.notChanged.map((r) => (
                  <li key={keyOf(r)} className="text-sm">
                    <span className="text-ele-strong">{r.label}</span>
                    <span className="mt-0.5 block text-xs text-ele-body">{r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={groupCls}>
            <h3 className="font-heading text-lg text-ele-strong">Files in Google Drive</h3>
            {preview.files.toDelete.length + preview.files.notOwned.length + preview.files.kept.length === 0 ? (
              <p className="text-xs text-ele-body">None of these records has a file in Drive.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {preview.files.toDelete.map((f) => (
                  <li key={`d-${f.fileId}`}>
                    Deleted: a file on {f.label}.{" "}
                    <a className="underline" href={f.driveUrl} target="_blank" rel="noreferrer">
                      Open in Drive
                    </a>
                  </li>
                ))}
                {preview.files.kept.map((f) => (
                  <li key={`k-${f.fileId}`}>Kept: a file on {f.label}, because the record it belongs to is kept.</li>
                ))}
                {preview.files.notOwned.map((f) => (
                  <li key={`n-${f.fileId}`}>
                    Not deleted: a file {f.label} points at that the platform did not file itself. Remove it in Drive by hand if it should go.{" "}
                    <a className="underline" href={f.driveUrl} target="_blank" rel="noreferrer">
                      Open in Drive
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className={noteCls}>{preview.xeroNote}</p>
          {preview.recreatable.map((r) => (
            <p key={r.model} className={noteCls}>
              {r.note}
            </p>
          ))}
          <p className={noteCls}>{preview.tombstone.note}</p>

          <div className="mt-5">
            <Button variant="destructive" onClick={() => setConfirmOpen(true)} disabled={stale || erasing || changes + groups.held.length === 0}>
              Erase
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div role="status" aria-label="Erasure result" className="mt-4 rounded-ele border border-ele-purple-accent p-3 text-sm">
          <p className="text-ele-strong">
            {result.alreadyCarriedOut
              ? "This erasure had already been carried out, so nothing was erased again. Only what was outstanding was tried again: the Drive files still to delete, and the record of the erasure."
              : `Erased: ${plural(result.deleted, "record", "records")} deleted, ${plural(result.anonymised, "record", "records")} anonymised, ${plural(result.held, "record", "records")} kept under a legal hold.`}
          </p>
          {result.filesDeleted > 0 && <p className="mt-1 text-ele-body">{plural(result.filesDeleted, "Drive file was", "Drive files were")} deleted.</p>}
          {result.fileFailures.length > 0 && (
            <div className="mt-2">
              <p className="text-ele-strong">Delete these files in Google Drive by hand:</p>
              <ul className="mt-1 space-y-1">
                {result.fileFailures.map((f) => (
                  <li key={f.fileId}>
                    A file on {f.label}.{" "}
                    <a className="underline" href={f.driveUrl} target="_blank" rel="noreferrer">
                      Open in Drive
                    </a>{" "}
                    <span className="text-xs text-ele-body">{f.error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.tombstone.error ? (
            <p className="mt-2 text-ele-strong">{result.tombstone.error}</p>
          ) : result.tombstone.recorded > 0 ? (
            <p className="mt-2 text-ele-body">
              A record that {[...result.tombstone.emails, ...(result.tombstone.phones ?? [])].join(", ")}{" "}
              {result.tombstone.emails.length + (result.tombstone.phones ?? []).length === 1 ? "was" : "were"} erased is kept, so the person is not
              brought back without a warning.
            </p>
          ) : null}
          {/* ONE retry for whatever is outstanding (Session ASQ, Item 3's review, F4): the Drive files, the record of the
              erasure, or both. It resends exactly the first commit, and is named for what it does. */}
          {committed && (result.fileFailures.length > 0 || result.tombstone.error) && (
            <Button className="mt-2" size="sm" variant="secondary" disabled={erasing} onClick={() => void commit(committed)}>
              {erasing ? "Trying again..." : result.tombstone.error ? "Save the erasure record again" : "Try the Drive deletes again"}
            </Button>
          )}
          <p className="mt-2 text-xs text-ele-body">Search again to see what is left on the platform for this person.</p>
        </div>
      )}

      <ConfirmDeleteModal
        open={confirmOpen && !!preview}
        variant="destructive"
        title="Erase this person's data?"
        message={confirmMessage}
        confirmLabel="Erase"
        busy={erasing}
        error={null}
        onConfirm={() => preview && void commit({ refs, planHash: preview.planHash, plannedAt: preview.plannedAt })}
        onCancel={() => {
          if (!erasing) setConfirmOpen(false);
        }}
      />
    </section>
  );
}

// ---- Lift an erasure record (admin) -----------------------------------------------------------------------------------------

/** What was typed, as the lift route takes it: an email address when it has an "@", otherwise a phone number. */
function liftIdentifier(value: string): { email: string } | { phone: string } | null {
  if (value.includes("@")) return { email: value };
  return value.replace(/\D/g, "").length >= 7 ? { phone: value } : null;
}

export function LiftErasureRecord() {
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GdprTombstoneLiftResult | null>(null);
  const address = email.trim();

  async function lift() {
    const identifier = liftIdentifier(address);
    if (!identifier) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await api.gdpr.tombstones.lift(identifier));
      setOpen(false);
      setEmail("");
    } catch (err) {
      setOpen(false);
      setError(friendlyApiError(err, "The erasure record could not be lifted. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  const scopeWords = (scope: "full" | "mailbox") => (scope === "full" ? "erased on this page" : "forgotten by the email engine");

  return (
    <section className={sectionCls} aria-label="Lift an erasure record">
      <h2 className="font-heading text-xl text-ele-blue">Lift an erasure record</h2>
      <p className="mt-1 text-sm text-ele-body">
        After an erasure the platform keeps a one-way record of each email address and phone number searched, so the person is not recorded or imported again
        without a warning. If they ask to be a customer or to be contacted again, lift it here, one address or number at a time.
      </p>
      <form
        className="mt-3 flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          setResult(null);
          if (!liftIdentifier(address)) {
            setError("Enter the full email address or phone number.");
            return;
          }
          setError(null);
          setOpen(true);
        }}
      >
        <input
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Email address or phone number whose erasure record should be lifted"
          className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green sm:max-w-xs"
        />
        <Button type="submit" variant="destructive" size="sm">
          Lift erasure record
        </Button>
      </form>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {result && (
        <p role="status" className="mt-2 text-sm text-ele-body">
          Lifted: {result.lifted.map((l) => `${scopeWords(l.scope)} on ${l.erasedOn}`).join(", ")}. The person is now recorded and imported without a warning.
        </p>
      )}
      <ConfirmDeleteModal
        open={open}
        variant="destructive"
        title="Lift the erasure record?"
        message={`If an erasure record is held for ${address}, it is removed. The person will then be recorded by the email engine, and brought back from Xero or Apollo or added as a new customer or prospect, without a warning.`}
        confirmLabel="Lift it"
        busy={busy}
        error={null}
        onConfirm={() => void lift()}
        onCancel={() => {
          if (!busy) setOpen(false);
        }}
      />
    </section>
  );
}
